import React from "react";
import { Skeleton } from "@/components/ui/skeleton";

/** Full page loading state: header, stat row and a content panel. */
export const PageSkeleton: React.FC = () => (
  <div aria-busy="true" aria-label="Loading">
    <Skeleton className="mb-2 h-8 w-56" />
    <Skeleton className="mb-8 h-4 w-80 max-w-full" />
    <StatCardsSkeleton />
    <div className="mt-6 rounded-md border border-border bg-card p-5">
      <Skeleton className="mb-5 h-5 w-44" />
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    </div>
  </div>
);

export const CardSkeleton: React.FC<{ lines?: number }> = ({ lines = 3 }) => (
  <div className="rounded-md border border-border bg-card p-5" aria-busy="true">
    <Skeleton className="mb-4 h-5 w-2/5" />
    <div className="space-y-2.5">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className="h-3.5" style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  </div>
);

export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4,
}) => (
  <div className="overflow-hidden rounded-md border border-border" aria-busy="true">
    <div className="flex gap-4 border-b border-border bg-card px-3 py-3">
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} className="h-3 flex-1" />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="flex gap-4 border-b border-border px-3 py-3.5 last:border-0">
        {Array.from({ length: columns }).map((_, c) => (
          <Skeleton key={c} className="h-4 flex-1" />
        ))}
      </div>
    ))}
  </div>
);

export const ListSkeleton: React.FC<{ items?: number }> = ({ items = 5 }) => (
  <div className="divide-y divide-border" aria-busy="true">
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 py-3">
        <Skeleton className="size-9 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3.5 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
    ))}
  </div>
);

export const FormSkeleton: React.FC<{ fields?: number }> = ({ fields = 4 }) => (
  <div className="space-y-5" aria-busy="true">
    {Array.from({ length: fields }).map((_, i) => (
      <div key={i} className="space-y-2">
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="h-9 w-full" />
      </div>
    ))}
    <Skeleton className="h-9 w-32" />
  </div>
);

export const GridSkeleton: React.FC<{ cards?: number }> = ({ cards = 6 }) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
    {Array.from({ length: cards }).map((_, i) => (
      <CardSkeleton key={i} />
    ))}
  </div>
);

export const StatCardsSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4" aria-busy="true">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="rounded-md border border-border bg-card p-4 md:p-5">
        <Skeleton className="mb-4 h-3 w-20" />
        <Skeleton className="h-8 w-16" />
      </div>
    ))}
  </div>
);
